import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinusCircle } from "@fortawesome/free-solid-svg-icons";

import {
  COLORS,
  messages,
  recapOptions,
  EMPTY_STRING,
  DEXIE_TABLENAME,
  ADD_RECAPZ_STEPS,
  MAX_IMAGE_SIZE_MB,
  ACCEPTED_IMAGE_TYPES,
} from "../../../../constants";
import {
  toBase64,
  customToast,
  getRecapPrice,
  getS3GalleryUrl,
  getReelsLogoName,
  isArrayWithItems,
  syncLogoDataWithAddRecapz,
  validateRecapzReelDetails,
  hasEmptyReelDetailDescription,
  isAddRecapzIdentical,
} from "../../../../shared";

import db from "../../../../dexieDB";
import { eventBookingActions } from "../../../../redux/actions";
import { useDexieSync } from "../../../../shared/hooks/useDexie";
import VideoModal from "../../../../shared/components/Modal/VideoModal";
import SelectField from "../../../../shared/components/Fields/SelectField";
import { ReactComponent as BackIcon } from "../../../../assets/icons/back.svg";
import { ReactComponent as PlusIcon } from "../../../../assets/icons/plus-square.svg";
import { ReactComponent as DeleteSvg } from "../../../../assets/icons/delete.svg";

/**
 * Returns true if the reel represented by `recapItem` has been delivered.
 *
 * `recapItem` here is the in-memory reel object built from addRecapz.
 * We cross-reference with editEvents.reels (the source of truth from the
 * backend) so that the flag survives any in-memory transformations.
 *
 * Falls back to the flag on recapItem itself when editEvents is unavailable.
 */
const isReelDelivered = (recapItem, editEvents) => {
  if (!recapItem) return false;

  const reels = editEvents?.reels ?? [];

  // recapItem.id may be either the top-level order item id (r.id)
  // or the reelDetail id (r.reelDetail.id) depending on how
  // transformReelsInEditEvent maps the data — check both.
  const backendReel = reels.find(
    (r) =>
      r.id === recapItem.id ||
      r.reelDetail?.id === recapItem.id ||
      r.reelDetail?.orderItemId === recapItem.id
  );

  if (backendReel) return !!backendReel.isReelDelivered;

  // Fallback: the flag may have been injected during transformReelsInEditEvent
  return !!recapItem.isReelDelivered;
};

const SelectRecapType = (props) => {
  const { t } = useTranslation("common");

  const {
    addRecapz,
    isEditEvent,
    setAddRecapz,
    handleActiveStep,
    useSameLogoForAllRecaps,
    setUseSameLogoForAllRecaps,
    addRecapzBackup,
    isAddFlow,
  } = props;

  // State for managing video url that is coming from backend
  const [mediaUrl, setMediaUrl] = useState(null);
  const [logoData, setLogoData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sync logoData state with Dexie.js
  // store the recap images in the indexedDB (browser storage) using Dexie.js
  useDexieSync(DEXIE_TABLENAME.LOGOS, logoData, setLogoData);

  // Sync logoData with addRecapz in the redux store
  // Also update the setLogoData if any recap is removed from addRecapz to update the indexedDB
  useEffect(() => {
    if (!logoData.length || !addRecapz.length) return;

    const updatedLogoData = syncLogoDataWithAddRecapz(logoData, addRecapz);

    if (JSON.stringify(updatedLogoData) !== JSON.stringify(logoData)) {
      setLogoData(updatedLogoData);
      if (!isArrayWithItems(updatedLogoData)) {
        (async () => {
          await db[DEXIE_TABLENAME.LOGOS].clear();
        })();
      }
    }
    // eslint-disable-next-line
  }, [addRecapz, logoData]);

  const handleRecapChanges = (eventId, recapId, updatedFields) => {
    const updated = addRecapz.map((item) => {
      if (item.eventId === eventId) {
        return {
          ...item,
          reelDetail: item.reelDetail.map((r) =>
            r.id === recapId ? { ...r, ...updatedFields } : r
          ),
        };
      }
      return item;
    });

    setAddRecapz(updated);
  };

  const handleLogoUpload = async (img, eventId, recapId) => {
    const fileInput = img.target;
    const file = fileInput.files[0];
    if (!file) return;

    const fileExtension = `.${file.type.split("/")[1]}`;
    const fileSizeMB = file.size / (1024 * 1024);

    if (!ACCEPTED_IMAGE_TYPES.includes(fileExtension)) {
      customToast(messages.UNSUPPORTED_IMAGE_FORMAT, toast.TYPE.ERROR);
      fileInput.value = ""; // reset input
      return;
    }

    if (fileSizeMB > MAX_IMAGE_SIZE_MB) {
      customToast(messages.IMAGE_SIZE_EXCEEDED, toast.TYPE.ERROR);
      fileInput.value = ""; // reset input
      return;
    }

    const base64 = await toBase64(file);

    const newLogoEntry = {
      eventId,
      recapId,
      raw: base64,
      name: file.name,
    };

    setLogoData((prev) => {
      const filtered = prev.filter(
        (item) => !(item.eventId === eventId && item.recapId === recapId)
      );
      return [...filtered, newLogoEntry];
    });

    let updatedAddRecapz = addRecapz.map((event) =>
      event.eventId === eventId
        ? {
            ...event,
            reelDetail: event.reelDetail.map((r) =>
              r.id === recapId ? { ...r, logoUrl: base64 } : r
            ),
          }
        : event
    );

    // If "use same logo for all" is active, also sync addRecapz here
    if (useSameLogoForAllRecaps) {
      updatedAddRecapz = updatedAddRecapz.map((event) => ({
        ...event,
        reelDetail: event.reelDetail.map((r) => ({ ...r, logoUrl: base64 })),
      }));
    }

    setAddRecapz(updatedAddRecapz);

    // Reset input so same file can trigger again
    fileInput.value = "";
  };

  const handleNextClick = () => {
    if (!isArrayWithItems(addRecapz)) return;

    const isIdenticalRecapz = !props.regenerateEditBriefs
      ? isAddRecapzIdentical(addRecapz, addRecapzBackup)
      : true;

    if (!validateRecapzReelDetails(addRecapz)) {
      customToast(
        messages.EACH_RECAP_MUST_HAVE_ORIENTATION_TYPE,
        toast.TYPE.ERROR
      );
      return;
    }

    if (
      hasEmptyReelDetailDescription(addRecapz) ||
      props.regenerateEditBriefs ||
      !isIdenticalRecapz
    ) {
      props.generateEditBrief();
      return;
    }

    handleActiveStep(ADD_RECAPZ_STEPS.RECAP_FORM);
  };

  const showButton =
    !isEditEvent &&
    (addRecapz.length > 1 ||
      addRecapz.some((item) => item.reelDetail && item.reelDetail.length > 1));

  const handleUseSameLogoForAllRecaps = (flag) => {
    setUseSameLogoForAllRecaps(flag);

    if (logoData.length > 0) {
      // Find the logo of the first recap in addRecapz
      const firstEvent = addRecapz[0];
      const firstRecap = firstEvent?.reelDetail?.[0];

      // Try to find matching logo from logoData based on eventId + recapId
      const firstLogo = logoData.find(
        (l) => l.eventId === firstEvent?.eventId && l.recapId === firstRecap?.id
      );

      // Keep only the first logo
      setLogoData(firstLogo ? [firstLogo] : []);

      const updatedAddRecapz = addRecapz.map((event, eventIndex) => ({
        ...event,
        reelDetail: event.reelDetail.map((recap, recapIndex) => {
          const isFirstRecap = eventIndex === 0 && recapIndex === 0;

          // If flag is true, assign first logo to all recaps
          if (flag) {
            return { ...recap, logoUrl: firstLogo ? firstLogo.raw : null };
          }

          // If flag is false, keep only the first recap's logo
          return {
            ...recap,
            logoUrl: isFirstRecap && firstLogo ? firstLogo.raw : null,
          };
        }),
      }));

      setAddRecapz(updatedAddRecapz);
    }
  };

  const handleRemoveLogo = (eventId, recapId) => {
    let updatedLogoData = logoData.filter(
      (item) => !(item.eventId === eventId && item.recapId === recapId)
    );

    let updatedAddRecapz = addRecapz.map((event) =>
      event.eventId === eventId
        ? {
            ...event,
            reelDetail: event.reelDetail.map((r) =>
              r.id === recapId ? { ...r, logoUrl: null } : r
            ),
          }
        : event
    );

    // If "use same logo for all" is active, also sync addRecapz here
    if (useSameLogoForAllRecaps) {
      updatedLogoData = [];
      updatedAddRecapz = updatedAddRecapz.map((event) => ({
        ...event,
        reelDetail: event.reelDetail.map((r) => ({ ...r, logoUrl: null })),
      }));
    }

    setLogoData(updatedLogoData);
    setAddRecapz(updatedAddRecapz);
  };

  const handleDeleteRecap = (eventId, recapId) => {
    const updatedLogoData = logoData.filter(
      (item) => !(item.eventId === eventId && item.recapId === recapId)
    );

    const updatedAddRecapz = addRecapz.map((event) => {
      if (event.eventId !== eventId) return event;

      const filteredReelDetail = (event.reelDetail ?? []).filter(
        (r) => r.id !== recapId
      );

      return {
        ...event,
        reelDetail: filteredReelDetail,
        quantity: Math.max(0, (event.quantity || 0) - 1),
      };
    });

    setLogoData(updatedLogoData);
    setAddRecapz(updatedAddRecapz);
  };

  // In the add-recap flow, only reels added through this session
  // (isAddFlowReel === true) should be shown/edited on this screen.
  // Pre-existing reels are left out of this list entirely here.
  const getVisibleReelDetail = (item) => {
    if (!isAddFlow) return item.reelDetail ?? [];
    return (item.reelDetail ?? []).filter((r) => r.isAddFlowReel === true);
  };

  return (
    <>
      <div className="select-recapz-type col-12">
        <p className="add-recapz-heading label-content-600 mb-2">
          {messages.LET_GET_YOU_EDIT_RIGHT}
        </p>
        <p className="add-recapz-description mb-2">
          {messages.TELL_US_WHAT_TYPE_OF_RECAP_YOU_WOULD_LIKE}
        </p>
        <p className="add-recapz-heading label-content-600 mb-2">
          {messages.THESE_ARE_THE_TYPES_OF_EDITS_WE_OFFER}
        </p>

        <div className="row mt-4">
          {recapOptions.map((recap, index) => {
            const price = getRecapPrice(recap.name, props.recapTypes);
            return (
              <div
                key={recap.id}
                className={`col-md-6 ${
                  index === 1 ? "mt-2 mt-md-0" : EMPTY_STRING
                }`}
              >
                <div className="recap-box text-center">
                  <p className="recap-box-title mb-1">{`${recap.title} - $${price}`}</p>
                  <p className="recap-box-description mb-0">
                    {recap.description}
                  </p>
                  <div className="">
                    <button
                      type="button"
                      className="recap-box-button"
                      onClick={(e) => {
                        e.preventDefault();
                        setMediaUrl(
                          getS3GalleryUrl(
                            props.recapTypes?.[index]?.url +
                              `?timestamp=${Date.now()}`
                          )
                        );
                        setIsModalOpen(true);
                      }}
                    >
                      {messages.VIEW_SAMPLE}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="sample-text-recap mt-3 mb-2">
          *{messages.Click_On_The_Tiles_Above_To_See_A_Sample_Of_Each_Recap}
        </p>

        <div className="event-recap-type-container">
          {showButton && (
            <div className="row mt-3 text-left align-items-start recap-type-box">
              <div className="col-12 col-lg-3 d-none d-lg-block"></div>
              <div className="col-12 col-lg-3 d-none d-lg-block"></div>
              <div className="col-12 col-lg-3 d-none d-lg-block"></div>
              <div className="col-12 col-lg-3">
                <div className="d-flex align-items-center justify-content-center justify-content-lg-start pt-2">
                  <input
                    type="checkbox"
                    name="useSameLogoForAllRecaps"
                    checked={useSameLogoForAllRecaps}
                    onChange={(e) => {
                      handleUseSameLogoForAllRecaps(!useSameLogoForAllRecaps);
                    }}
                    className="custom-checkout logo-checkbox"
                  />
                  <p className="label-content fw-500 mb-0 ml-2 logo-checkbox-text">
                    Use same logo for all
                  </p>
                </div>
              </div>
            </div>
          )}
          {addRecapz.map((item, recapIndex) =>
            getVisibleReelDetail(item).map((recapItem, recapItemIndex) => {
              const delivered = isReelDelivered(recapItem, props.editEvents);
              // Logo upload should also be locked when the "same logo" toggle
              // covers this row (existing logic) OR when the reel is delivered.
              const isLogoDisabled =
                delivered ||
                (useSameLogoForAllRecaps &&
                  !(recapIndex === 0 && recapItemIndex === 0));

              return (
                <div
                  key={`${item.eventId}-${recapItem.id}`}
                  className={`row mt-3 text-left align-items-start recap-type-box${
                    delivered ? " recap-type-box--delivered" : EMPTY_STRING
                  }`}
                >
                  <div className="col-12 col-lg-3 d-flex align-items-center text-center text-lg-left">
                    <strong className="recap-name label-content-600 text-left">
                      {recapItem?.reelName}
                    </strong>
                    <div>
                      {delivered && (
                        <span className="ml-2 badge badge-success">
                          completed
                        </span>
                      )}
                    </div>
                    {!delivered && (
                      <span
                        className="ml-2 cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                          handleDeleteRecap(item.eventId, recapItem.id)
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            handleDeleteRecap(item.eventId, recapItem.id);
                          }
                        }}
                      >
                        <DeleteSvg />
                      </span>
                    )}
                  </div>

                  {/* Recap type (radio) */}
                  <div className="col-12 col-lg-3 recap-type-fields">
                    <div className="recap-radio-group">
                      {props.recapTypes?.map((recapType) => (
                        <label
                          key={recapType.id}
                          className={`recap-radio-title mb-0${
                            delivered ? " disabled" : EMPTY_STRING
                          }`}
                        >
                          <input
                            type="radio"
                            className="custom-checkout recap-type-radio"
                            value={recapType.name}
                            name={`recap-${item.eventId}-${recapItem.id}`}
                            checked={recapItem.reelMode === recapType.name}
                            disabled={delivered}
                            onChange={() =>
                              handleRecapChanges(item.eventId, recapItem.id, {
                                reelMode: recapType.name,
                                price: recapType.price,
                              })
                            }
                          />
                          <span
                            className={`text-lowercase ${delivered ? "text-muted cursor-not-allowed" : EMPTY_STRING}`}
                          >
                            {recapType.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Orientation dropdown */}
                  <div className="col-12 col-lg-3 recap-type-fields">
                    <SelectField
                      options={window.config?.orientationOption}
                      id="select-orientation"
                      className="orientation-dropdown"
                      placeholder="select orientation"
                      value={recapItem?.orientation || EMPTY_STRING}
                      setValue={(e) =>
                        handleRecapChanges(item.eventId, recapItem.id, {
                          orientation: e.value,
                        })
                      }
                      // Pass disabled prop — ensure SelectField forwards it to
                      // the underlying select element
                      disabled={delivered}
                    />
                  </div>

                  {/* Logo upload */}
                  <div className="col-12 col-lg-3 recap-type-fields">
                    <div
                      className={`logo-upload-input ${
                        isLogoDisabled ? "disabled" : EMPTY_STRING
                      }`}
                    >
                      <label
                        htmlFor={`logo-${item.eventId}-${recapItem.id}`}
                        className={`form-label label-content-600 w-100${
                          isLogoDisabled ? " cursor-not-allowed" : EMPTY_STRING
                        }`}
                      >
                        <div className="d-flex align-items-center image-text">
                          <span className="add-icon">
                            <PlusIcon />
                          </span>
                          <span className="text-content">
                            click here to upload logo
                          </span>
                        </div>
                      </label>
                      <input
                        type="file"
                        name="logo"
                        title="logo"
                        id={`logo-${item.eventId}-${recapItem.id}`}
                        accept={ACCEPTED_IMAGE_TYPES}
                        onChange={(e) =>
                          handleLogoUpload(e, item.eventId, recapItem.id)
                        }
                        disabled={isLogoDisabled}
                        hidden
                      />
                      <div className="text-muted preview-image ellipsis-text">
                        {getReelsLogoName(recapItem, logoData, item) && (
                          <FontAwesomeIcon
                            icon={faMinusCircle}
                            color={COLORS.Red}
                            className={`mr-2${
                              delivered
                                ? " cursor-not-allowed"
                                : " cursor-pointer"
                            }`}
                            onClick={() =>
                              !delivered &&
                              handleRemoveLogo(item.eventId, recapItem.id)
                            }
                          />
                        )}
                        {getReelsLogoName(recapItem, logoData, item)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="d-flex justify-content-center sticky-button-row mt-5">
          <button
            type="button"
            className="event-back-btn button-common button-outline btn-text-grey width-50"
            onClick={() =>
              handleActiveStep(ADD_RECAPZ_STEPS.SELECT_EVENTS_QUANTITY)
            }
          >
            <BackIcon />
            <span className="pl-1">{t("go_back")}</span>
          </button>
          <button
            type="button"
            className="button-common button-fill width-50 ml-3"
            onClick={() => {
              handleNextClick();
            }}
          >
            {t("next")}
          </button>
        </div>

        <p className="text-content line-height-normal mt-2">
          price includes one round of minor revisions.
          <br />
          includes 1 music change, removing or replacing up to 3 clips. feedback
          beyond may incur incremental costs price includes one round of minor
          revisions.
        </p>
      </div>

      <VideoModal
        videoUrl={mediaUrl}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  addRecapz: state.eventBooking.addRecapz,
  addRecapzBackup: state.eventBooking.addRecapzBackup,
  recapTypes: state.eventBooking.recapTypes,
  isEditEvent: state.eventBooking.isEditEvent,
  editEvents: state.eventBooking.editEvents,
  regenerateEditBriefs: state.eventBooking.regenerateEditBriefs,
  useSameLogoForAllRecaps: state.eventBooking.useSameLogoForAllRecaps,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  setAddRecapz: (data) => dispatch(eventBookingActions.setAddRecapz(data)),
  setUseSameLogoForAllRecaps: (data) =>
    dispatch(eventBookingActions.setUseSameLogoForAllRecaps(data)),
  setRegenerateEditBriefs: (data) =>
    dispatch(eventBookingActions.setRegenerateEditBriefs(data)),
});

// mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(SelectRecapType);
